import { jwtDecode } from 'jwt-decode'
import apiClient, {
  getToken,
  setToken,
  setRefreshToken,
  clearTokens,
} from '../api/client'
import { API_CONFIG } from '../config/api.config'
import type {
  User,
  LoginRequest,
  LoginResponse,
  SignupRequest,
  SignupResponse,
} from '@/types/auth.types'

const USER_KEY = 'qxproveit_user'

interface JWTPayload {
  sub: string
  login: string
  tenant_id: string
  organization_id: string
  exp: number
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      API_CONFIG.ENDPOINTS.AUTH.LOGIN,
      credentials
    )

    setToken(data.access_token)
    setRefreshToken(data.refresh_token)
    this.setStoredUser(data.user)

    return data
  }

  async signup(userData: SignupRequest): Promise<SignupResponse> {
    const { data } = await apiClient.post<SignupResponse>(
      API_CONFIG.ENDPOINTS.AUTH.SIGNUP,
      userData
    )

    setToken(data.access_token)
    setRefreshToken(data.refresh_token)
    this.setStoredUser(data.user)

    return data
  }

  async getCurrentUser(): Promise<User> {
    console.log('authService.getCurrentUser - Making API call to', API_CONFIG.ENDPOINTS.AUTH.ME)
    const { data } = await apiClient.get<User>(
      API_CONFIG.ENDPOINTS.AUTH.ME
    )
    console.log('authService.getCurrentUser - Received data:', data)
    this.setStoredUser(data)
    return data
  }

  logout(): void {
    clearTokens()
    localStorage.removeItem(USER_KEY)
    window.location.href = '/login'
  }

  getStoredUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY)
    if (!userStr) return null
    try {
      return JSON.parse(userStr) as User
    } catch {
      return null
    }
  }

  setStoredUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  }

  isAuthenticated(): boolean {
    const token = getToken()
    if (!token) return false

    try {
      const decoded = jwtDecode<JWTPayload>(token)
      return decoded.exp * 1000 > Date.now()
    } catch {
      return false
    }
  }

  getUserFromToken(): Partial<User> | null {
    const token = getToken()
    if (!token) return null

    try {
      const decoded = jwtDecode<JWTPayload>(token)
      return {
        id: decoded.sub,
        login: decoded.login,
        tenant_id: decoded.tenant_id,
        organization_id: decoded.organization_id,
      }
    } catch {
      return null
    }
  }
}

export const authService = new AuthService()
export default authService
